import { CommonModule } from '@angular/common';
import { Component, computed, input, output, signal } from '@angular/core';
import { OuiButtonComponent } from '@aiz-gl/omni-ui';

export type ImageAttachment = {
  id: string;
  name: string;
};

@Component({
  selector: 'app-image-attachment-slider',
  standalone: true,
  imports: [CommonModule, OuiButtonComponent],
  templateUrl: './image-attachment-slider.component.html',
  styleUrl: './image-attachment-slider.component.css',
})
export class ImageAttachmentSliderComponent {
  readonly attachments = input<ImageAttachment[]>([]);
  readonly loading = input(false);
  readonly imagesLoading = input(false);
  readonly thumbnailUrls = input<Record<string, string>>({});
  readonly downloadAttachment = output<ImageAttachment>();
  readonly viewerOpened = output<number>();
  readonly viewerClosed = output<void>();
  readonly selectedIndex = signal<number | null>(null);

  readonly selectedAttachment = computed(() => {
    const index = this.selectedIndex();
    const items = this.attachments();
    return index === null ? null : items[index] ?? null;
  });

  readonly thumbnailTrackTransform = computed(() => {
    const selectedIndex = this.selectedIndex() ?? 0;
    const itemOffset = Math.max(0, selectedIndex - 3);
    return `translateX(-${itemOffset * 72}px)`;
  });

  open(index: number) {
    if (index >= 0 && index < this.attachments().length) {
      const wasClosed = this.selectedIndex() === null;
      if (!wasClosed && this.imagesLoading()) {
        return;
      }

      this.selectedIndex.set(index);
      const attachment = this.attachments()[index];
      if (wasClosed && attachment) {
        this.viewerOpened.emit(index);
      }
    }
  }

  close() {
    this.selectedIndex.set(null);
    this.viewerClosed.emit();
  }

  previous() {
    const index = this.selectedIndex();
    if (index === null || index === 0 || this.imagesLoading()) {
      return;
    }

    this.open(index - 1);
  }

  next() {
    const index = this.selectedIndex();
    const count = this.attachments().length;
    if (index === null || count === 0 || this.imagesLoading()) {
      return;
    }

    const nextIndex = index + 1;
    if (nextIndex >= count) {
      return;
    }

    this.selectedIndex.set(nextIndex);
  }

  download() {
    const attachment = this.selectedAttachment();
    if (!attachment || this.imagesLoading()) {
      return;
    }

    this.downloadAttachment.emit(attachment);
  }

  getFileName(attachment: ImageAttachment): string {
    return attachment.name.split('?')[0].split('/').filter(Boolean).pop() || attachment.name;
  }

  isImage(attachment: ImageAttachment): boolean {
    return /\.(gif|jpe?g|png|webp|bmp|svg)$/i.test(attachment.name.split('?')[0]);
  }

  getThumbnailUrl(attachment: ImageAttachment): string | undefined {
    return this.thumbnailUrls()[attachment.id];
  }
}
